import { z } from "zod";

export const ALLOWED_FORM_DRAFT_INPUT_KEYS = [
    "scale-rating",
    "single-choice",
    "multiple-choice",
    "yes-no",
    "checkbox",
    "dropdown",
    "short-text",
    "long-text",
    "number",
    "slider",
    "date-picker",
] as const;

export type AllowedFormDraftInputKey = (typeof ALLOWED_FORM_DRAFT_INPUT_KEYS)[number];

export const FORM_DRAFT_PROMPT_MAX_CHARS = 2000;
export const FORM_DRAFT_MAX_PAGES = 12;
export const FORM_DRAFT_MAX_INPUTS_PER_PAGE = 20;

export const DEFAULT_SCALE_OPTIONS = [
    { label: "Strongly agree", value: 4 },
    { label: "Agree", value: 3 },
    { label: "Neutral", value: 2 },
    { label: "Disagree", value: 1 },
    { label: "Strongly disagree", value: 0 },
] as const;

const LICENSED_PROM_NEEDLES = [
    "womac",
    "eq-5d",
    "eq5d",
    "eq 5d",
    "koos",
    "oxford knee",
    "oxford hip",
    "oxford shoulder",
    "moxfq",
    "visa-a",
    "visa a",
    "visa-g",
    "visa-h",
    "visa-p",
    "faam",
    "quickdash",
    "quick dash",
    "cumberland",
    "psfs",
];

export interface FormDraftScaleOption {
    label: string;
    value: number;
}

export interface FormDraftInput {
    input_id?: string;
    input_key: AllowedFormDraftInputKey;
    input_label: string;
    input_description?: string;
    is_optional?: boolean;
    options?: string[];
    scale_options?: FormDraftScaleOption[];
    min_value?: number;
    max_value?: number;
}

export interface FormDraftPage {
    page_id?: string;
    page_label: string;
    page_description?: string;
    inputs: FormDraftInput[];
}

export interface FormDraft {
    form_name: string;
    form_description: string;
    pages: FormDraftPage[];
}

export interface SanitiseFormDraftResult {
    draft: FormDraft | null;
    warnings: string[];
}

const ALLOWED_KEY_SET = new Set<string>(ALLOWED_FORM_DRAFT_INPUT_KEYS);
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function asRecord(value: unknown): Record<string, unknown> | null {
    if (value && typeof value === "object" && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }
    return null;
}

function asString(value: unknown, fallback = ""): string {
    return typeof value === "string" ? value.trim() : fallback;
}

function asNumber(value: unknown): number | undefined {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return undefined;
}

function asUuid(value: unknown): string | undefined {
    return typeof value === "string" && UUID_PATTERN.test(value) ? value : undefined;
}

function normaliseName(value: string): string {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}

function containsPhrase(haystack: string, needle: string): boolean {
    if (!needle) return false;
    return ` ${haystack} `.includes(` ${needle} `);
}

const LICENSED_PROM_LABELS: Record<string, string> = {
    womac: "WOMAC",
    "eq-5d": "EQ-5D",
    eq5d: "EQ-5D",
    "eq 5d": "EQ-5D",
    koos: "KOOS",
    "oxford knee": "Oxford Knee",
    "oxford hip": "Oxford Hip",
    "oxford shoulder": "Oxford Shoulder",
    moxfq: "MOXFQ",
    "visa-a": "VISA",
    "visa a": "VISA",
    "visa-g": "VISA",
    "visa-h": "VISA",
    "visa-p": "VISA",
    faam: "FAAM",
    quickdash: "QuickDASH",
    "quick dash": "QuickDASH",
    cumberland: "Cumberland",
    psfs: "PSFS",
};

export function isLicensedPromName(name: string): boolean {
    return getLicensedPromCatalogueWarning(name) != null;
}

export function getLicensedPromCatalogueWarning(text: string): string | null {
    const normalised = normaliseName(text);
    if (!normalised) return null;
    const needle = LICENSED_PROM_NEEDLES.find((item) => containsPhrase(normalised, normaliseName(item)));
    if (!needle) return null;
    const label = LICENSED_PROM_LABELS[needle] ?? needle.toUpperCase();
    return `${label} is a licensed questionnaire. Use the existing form from the catalogue instead of drafting a copy.`;
}

export function stripJsonFence(raw: string): string {
    const trimmed = raw.trim();
    const fenced = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
    return fenced?.[1]?.trim() ?? trimmed;
}

function sanitiseScaleOptions(raw: unknown): FormDraftScaleOption[] {
    if (!Array.isArray(raw) || raw.length === 0) {
        return DEFAULT_SCALE_OPTIONS.map((option) => ({ ...option }));
    }
    const options = raw
        .map((item, index) => {
            const record = asRecord(item);
            const label = asString(record?.label);
            const value = asNumber(record?.value) ?? index;
            if (!label) return null;
            return { label, value };
        })
        .filter((item): item is FormDraftScaleOption => item != null);
    return options.length > 0 ? options : DEFAULT_SCALE_OPTIONS.map((option) => ({ ...option }));
}

function sanitiseChoiceOptions(raw: unknown): string[] {
    if (!Array.isArray(raw)) return ["Yes", "No"];
    const options = raw.map((item) => asString(item)).filter(Boolean);
    return options.length > 0 ? options.slice(0, 20) : ["Yes", "No"];
}

export function sanitiseFormDraft(raw: unknown): SanitiseFormDraftResult {
    const warnings: string[] = [];
    const record = asRecord(raw);
    if (!record) {
        return { draft: null, warnings: ["The draft was empty or invalid."] };
    }

    let formName = asString(record.form_name) || "Custom questionnaire";
    if (isLicensedPromName(formName)) {
        formName = "Custom questionnaire";
    }

    const formDescription = asString(record.form_description);
    const licensedWarning = getLicensedPromCatalogueWarning(
        [asString(record.form_name), formDescription, JSON.stringify(record.pages ?? [])].join(" ")
    );
    if (licensedWarning) warnings.push(licensedWarning);
    const rawPages = Array.isArray(record.pages) ? record.pages.slice(0, FORM_DRAFT_MAX_PAGES) : [];
    if (Array.isArray(record.pages) && record.pages.length > FORM_DRAFT_MAX_PAGES) {
        warnings.push(`Kept the first ${FORM_DRAFT_MAX_PAGES} pages.`);
    }

    const pages: FormDraftPage[] = [];
    for (const rawPage of rawPages) {
        const pageRecord = asRecord(rawPage);
        if (!pageRecord) continue;
        const pageLabel = asString(pageRecord.page_label) || `Page ${pages.length + 1}`;
        const pageDescription = asString(pageRecord.page_description).slice(0, 500) || undefined;
        const rawInputs = Array.isArray(pageRecord.inputs)
            ? pageRecord.inputs.slice(0, FORM_DRAFT_MAX_INPUTS_PER_PAGE)
            : [];
        const inputs: FormDraftInput[] = [];

        for (const rawInput of rawInputs) {
            const inputRecord = asRecord(rawInput);
            if (!inputRecord) continue;
            const key = asString(inputRecord.input_key);
            if (!ALLOWED_KEY_SET.has(key)) {
                warnings.push(`Removed unsupported question type "${key || "unknown"}".`);
                continue;
            }
            const label = asString(inputRecord.input_label);
            if (!label) continue;

            const input: FormDraftInput = {
                input_id: asUuid(inputRecord.input_id),
                input_key: key as AllowedFormDraftInputKey,
                input_label: label,
                input_description: asString(inputRecord.input_description) || undefined,
                is_optional: inputRecord.is_optional === true,
            };

            if (key === "scale-rating") {
                input.scale_options = sanitiseScaleOptions(inputRecord.scale_options);
            }
            if (key === "single-choice" || key === "multiple-choice" || key === "dropdown") {
                input.options = sanitiseChoiceOptions(inputRecord.options);
            }
            if (key === "number") {
                input.min_value = asNumber(inputRecord.min_value);
                input.max_value = asNumber(inputRecord.max_value);
            }
            if (key === "slider") {
                const minValue = asNumber(inputRecord.min_value) ?? 0;
                const maxValue = asNumber(inputRecord.max_value) ?? 10;
                input.min_value = minValue;
                input.max_value = maxValue > minValue ? maxValue : minValue + 10;
            }
            inputs.push(input);
        }

        if (inputs.length > 0) {
            pages.push({
                page_id: asUuid(pageRecord.page_id),
                page_label: pageLabel,
                page_description: pageDescription,
                inputs,
            });
        }
    }

    if (pages.length === 0) {
        return {
            draft: null,
            warnings: warnings.length > 0 ? warnings : ["The draft had no usable questions."],
        };
    }

    return {
        draft: {
            form_name: formName.slice(0, 120),
            form_description: formDescription.slice(0, 500),
            pages,
        },
        warnings: [...new Set(warnings)],
    };
}

export function parseFormDraftJson(rawText: string): SanitiseFormDraftResult {
    try {
        const parsed: unknown = JSON.parse(stripJsonFence(rawText));
        return sanitiseFormDraft(parsed);
    } catch {
        return { draft: null, warnings: ["The model did not return valid JSON."] };
    }
}

const allowedInputKeySchema = z.enum(ALLOWED_FORM_DRAFT_INPUT_KEYS);

export const formDraftScaleOptionSchema = z.object({
    label: z.string().min(1),
    value: z.number(),
});

export const formDraftInputSchema = z
    .object({
        input_id: z.string().uuid().optional(),
        input_key: allowedInputKeySchema,
        input_label: z.string().trim().min(1, "Every question needs a label."),
        input_description: z.string().optional(),
        is_optional: z.boolean().optional(),
        options: z.array(z.string()).optional(),
        scale_options: z.array(formDraftScaleOptionSchema).optional(),
        min_value: z.number().optional(),
        max_value: z.number().optional(),
    })
    .superRefine((input, context) => {
        if (
            (input.input_key === "single-choice" ||
                input.input_key === "multiple-choice" ||
                input.input_key === "dropdown") &&
            (!input.options?.length || input.options.some((option) => option.trim().length === 0))
        ) {
            context.addIssue({
                code: "custom",
                path: ["options"],
                message: "Choice questions need at least one non-empty option.",
            });
        }
        if (
            input.input_key === "scale-rating" &&
            (!input.scale_options?.length || input.scale_options.some((option) => option.label.trim().length === 0))
        ) {
            context.addIssue({
                code: "custom",
                path: ["scale_options"],
                message: "Scale questions need at least one labelled option.",
            });
        }
        if (input.input_key === "number" || input.input_key === "slider") {
            if (input.min_value != null && input.max_value != null && input.max_value <= input.min_value) {
                context.addIssue({
                    code: "custom",
                    path: ["max_value"],
                    message: "The maximum value must be greater than the minimum value.",
                });
            }
        }
    });

export const formDraftPageSchema = z.object({
    page_id: z.string().uuid().optional(),
    page_label: z.string().trim().min(1, "Every page needs a title."),
    page_description: z.string().max(500).optional(),
    inputs: z.array(formDraftInputSchema).min(1).max(FORM_DRAFT_MAX_INPUTS_PER_PAGE),
});

export const formDraftSchema = z.object({
    form_name: z.string().trim().min(1, "The form needs a name.").max(120),
    form_description: z.string().max(500),
    pages: z.array(formDraftPageSchema).min(1).max(FORM_DRAFT_MAX_PAGES),
});
